import * as React from 'react';
import { styled, type StyledDecorator } from '@glitz/react';
import { Button } from 'webapp/components/button';
import { Position, useOnClickOutside, Flyout, animate, currentFreezeScrollPosition } from '@avensia/excite-ui';
import { Theme, isSvenssons } from 'webapp/theme';
import { headerHeight } from './general-styling';
import { offsetTop, scrollPosition } from '@avensia/excite';
import { FieldSize } from 'webapp/components/field-size';
import { ZIndex } from 'webapp/z-index';
import { overflowYDecorator } from 'webapp/decorators/overflow';
import { bareListDecorator } from 'webapp/decorators/list';
import Facet, { FacetFlyoutHeader } from './Facet';
import { RangeFacet } from './RangeFacet';
import { BooleanFacet } from './BooleanFacet';
import { BackIcon, CloseIcon, FilterIcon } from 'webapp/components/icons';
import { createButtonHoverDecorator } from 'webapp/utils/button-hover';
import { useSubPageFacetsSelection } from 'webapp/seo-subpages';
import { addFilterInteractionEventToDataLayer } from 'webapp/tracking/events/filter-interaction';
import { createMaxSmallViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import { scrollbarDecorator } from 'webapp/decorators/scrollbar';
import { DESKTOP_HEADER_HEIGHT, HEADER_HEIGHT, HEADER_HEIGHT_WITH_SEARCH } from 'webapp/utils/constants';
import { CountAndSort } from './CountAndSort';
import { HighlightedFacets } from './HighlightedFacets';
import { SelectedFacets } from './SelectedFacets';
import { white } from 'webapp/shared/sharedTheme';
import { desktopEdgeMarginDecorator } from 'webapp/components/page-layout';
import { useShowElementOnScrollUp } from 'webapp/use-on-scroll-direction-change';
import { updateUrlWithSortBySearchParams } from '@avensia/excite-esales';
import { addSortInteractionEventToDataLayer } from 'webapp/tracking/events/sort-interaction';
import { Sort } from './Sort';
import { NnTestIdProps } from 'webapp/shared/test-ids';
import { quickSearchShowObservable } from 'webapp/shared/use-show-quick-search-on-scroll';
import { currentUrl, translate, useMatchLargeViewport, useObservable } from '@avensia/excite';

const TRANSLATE_X_LEFT = -100;
const TRANSLATE_X_RIGHT = 100;

function transformEnterForm(isPush: boolean) {
  return `translateX(${isPush ? TRANSLATE_X_RIGHT : TRANSLATE_X_LEFT}%)`;
}

function transformLeave(isPush: boolean) {
  return `translateX(${isPush ? TRANSLATE_X_LEFT : TRANSLATE_X_RIGHT}%)`;
}

function transformEnter() {
  return 'translateX(0)';
}

type PropType = {
  facets: Excite.Esales.FacetApiModel[];
  highlightedFacets?: Excite.Esales.FacetApiModel[];
  totalProductCount: number;
  subPageData?: NordicNest.Api.ProductListingPageApiModel['subPageData'];
  selectedFacets?: Excite.Esales.FacetApiModel[];
  sortOptions: NordicNest.Api.SelectOptionApiModel[];
  containerRef: React.RefObject<HTMLDivElement | null>;
  contentWidthDecorator?: StyledDecorator;
};

export const Filter = React.memo((props: PropType) => {
  const showQuickSearch = useObservable(quickSearchShowObservable);
  const isDesktop = useMatchLargeViewport();
  const [open, setOpen] = React.useState(false);
  const enterRef = React.useRef<HTMLDivElement>(null);
  const leavingRef = React.useRef<HTMLDivElement>(null);
  const shouldAnimate = React.useRef(true);
  const [facets, onFacetValueChange, clearFacetValues] = useSubPageFacetsSelection(props.facets, props.subPageData);
  const [openFacetKey, setOpenFacetKey] = React.useState<string | undefined>();
  const previousPrimaryKey = React.useRef(openFacetKey);
  const previousFacet = facets.find(facet => facet.attribute === previousPrimaryKey.current);
  const filterRef = React.useRef<HTMLDivElement>(null);
  const [sortOptionsIsOpen, setOptionsIsOpen] = React.useState(false);
  const previousActiveElement = React.useRef<HTMLElement | null>(null);

  const [showStickyFilter, isSticky] = useShowElementOnScrollUp<HTMLDivElement>(
    props.containerRef,
    filterRef,
    open || (!isDesktop && sortOptionsIsOpen),
  );
  const showHighlightedFacets = props.highlightedFacets && props.highlightedFacets.length > 0;
  const showSelectedFacets = props.selectedFacets && props.selectedFacets.length > 0;
  const sortRef = React.useRef<HTMLDivElement>(null);

  const queryString = currentUrl().search;
  const urlParams = new URLSearchParams(queryString);
  const sortByUrlParamValue = urlParams.get('sortBy');

  const [sortOption, setSortOption] = React.useState(
    sortByUrlParamValue
      ? props.sortOptions.find(option => option.value === sortByUrlParamValue)?.text
      : props.sortOptions[0].text,
  );
  useOnClickOutside(sortRef, () => setOptionsIsOpen(false));

  const onChangeSort = React.useCallback(
    (value: string, text: string) => {
      updateUrlWithSortBySearchParams(value);
      addSortInteractionEventToDataLayer({
        value,
        products: props.totalProductCount,
      });
      setSortOption(text);
    },
    [props.totalProductCount],
  );

  const scrollToTop = React.useCallback(() => {
    if (props.containerRef.current) {
      const scroll =
        offsetTop(props.containerRef.current) -
        (isDesktop ? DESKTOP_HEADER_HEIGHT : HEADER_HEIGHT_WITH_SEARCH) -
        filterRef.current?.clientHeight!;

      currentFreezeScrollPosition.emit(scroll);
      scrollPosition(scroll);
    }
  }, [props.containerRef, isDesktop]);

  const onFacetChange = React.useCallback(
    async (
      facet: Excite.Esales.FacetApiModel,
      value: Excite.Esales.FacetValueApiModel | Excite.Esales.RangeApiModel,
    ) => {
      await onFacetValueChange(facet);
      scrollToTop();
      addFilterInteractionEventToDataLayer({
        facets,
        facet,
        value: 'text' in value ? value.text : value,
      });
    },
    [facets, onFacetValueChange, scrollToTop],
  );

  const onClearFacets = React.useCallback(() => {
    clearFacetValues();
    scrollToTop();
    addFilterInteractionEventToDataLayer({
      facets,
      action: 'reset',
    });
  }, [facets, clearFacetValues, scrollToTop]);

  const closeFlyout = React.useCallback(() => {
    setOpen(false);
  }, []);

  const openFlyout = React.useCallback(() => {
    previousActiveElement.current = document.activeElement as HTMLElement;
    setOpen(true);
  }, []);

  const close = React.useCallback(() => {
    setOpenFacetKey(undefined);
    previousPrimaryKey.current = undefined;
    //Need to set focus in a timeout because of some magic in trapTabbing
    setTimeout(() => {
      if (document.body.contains(previousActiveElement.current)) {
        previousActiveElement.current?.focus();
      }
    }, 100);
    closeFlyout();
  }, [closeFlyout]);

  React.useLayoutEffect(() => {
    if (open) {
      const isPush = Boolean(openFacetKey);
      const enteringElement = enterRef.current!;
      const animationOptions: KeyframeAnimationOptions = {
        fill: 'forwards',
        duration: shouldAnimate.current ? 300 : 0,
      };
      animate(
        enteringElement,
        [{ transform: transformEnterForm(isPush) }, { transform: transformEnter() }],
        animationOptions,
      );

      const leavingElement = leavingRef.current!;
      animate(
        leavingElement,
        [{ transform: transformEnter() }, { transform: transformLeave(isPush) }],
        animationOptions,
      );
    }
  }, [openFacetKey]);

  const facetRootHeader = (
    <Header>
      <HeaderText>{translate(t => t.productListing.filterButtonText)}</HeaderText>
      <CloseButton square onClick={close} disableRippleEffect as={Theme.Transparent}>
        <Close />
      </CloseButton>
    </Header>
  );

  const back = React.useCallback(() => {
    shouldAnimate.current = true;
    setOpenFacetKey(undefined);
  }, []);

  const facetHeader = previousFacet ? (
    <Header>
      <Back square disableRippleEffect onClick={back} as={Theme.Transparent} {...NnTestIdProps.FilterFlyoutBackButton}>
        <BackIcon />
      </Back>
      <HeaderText>
        {translate(t => t.productListing.filterValuesHeading, {
          ['current_filter']: previousFacet.text,
        })}
      </HeaderText>
      <CloseButton square disableRippleEffect onClick={close} as={Theme.Transparent}>
        <Close />
      </CloseButton>
    </Header>
  ) : null;

  const facetList = (
    <FacetList>
      {facets.map(facet => {
        if (facet.isRange) {
          return <RangeFacet key={facet.attribute} facet={facet} onFacetValueChange={onFacetChange} />;
        } else if (facet.values[0]?.text === 'true' || facet.values[0]?.text === 'false') {
          return <BooleanFacet key={facet.attribute} facet={facet} onFacetValueChange={onFacetChange} />;
        } else {
          return (
            <FacetFlyoutHeader
              key={facet.attribute}
              facet={facet}
              onClick={() => {
                shouldAnimate.current = true;
                previousPrimaryKey.current = facet.attribute;
                setOpenFacetKey(facet.attribute);
              }}
            />
          );
        }
      })}
    </FacetList>
  );

  const facet = previousFacet ? <Facet facet={previousFacet} onFacetValueChange={onFacetChange} /> : null;

  const openHighlightedFacet = React.useCallback(
    (facet: Excite.Esales.FacetApiModel) => {
      shouldAnimate.current = false;
      const openRoot = facet.isRange || facet.attribute === 'instock' || facet.attribute === 'withlid';
      previousPrimaryKey.current = openRoot ? undefined : facet.attribute;
      setOpenFacetKey(openRoot ? undefined : facet.attribute);
      openFlyout();
    },
    [openFlyout],
  );

  return (
    <>
      <Base
        ref={filterRef}
        css={styled(
          showStickyFilter ? undefined : hideElement,
          isSticky ? (showQuickSearch && !isDesktop ? stickyWithQuickSearchDecorator : stickyDecorator) : undefined,
        )}
      >
        <Content
          css={styled(
            !showHighlightedFacets && !showSelectedFacets ? gridAreaDecorator : undefined,
            showSelectedFacets ? selectedgridAreaDecorator : undefined,
            showHighlightedFacets || showSelectedFacets ? paddingDecorator : undefined,
            props.contentWidthDecorator,
          )}
        >
          <FacetsButton
            size={isDesktop ? FieldSize.Small : FieldSize.XSmall}
            as={Theme.FilterButton}
            onClick={openFlyout}
            css={(isDesktop || !showHighlightedFacets) && buttonDecorator}
            {...NnTestIdProps.PlpFilterButton}
          >
            <FilterButtonIcon />
            {(isDesktop || !showHighlightedFacets) && translate(t => t.productListing.filterButtonText)}
          </FacetsButton>
          {showHighlightedFacets && (
            <HighlightedFacets
              highlightedFacets={props.highlightedFacets}
              selectedFacets={props.selectedFacets}
              openHighlightedFacet={openHighlightedFacet}
              onFacetValueChange={onFacetChange}
            />
          )}
          {showSelectedFacets && (
            <SelectedFacets
              selectedFacets={props.selectedFacets}
              onFacetChange={onFacetChange}
              clearAllFacets={onClearFacets}
            />
          )}
          {(isDesktop || !isSticky || !(showHighlightedFacets || showSelectedFacets)) && (
            <CountAndSort
              setOptionsIsOpen={setOptionsIsOpen}
              sortOption={sortOption}
              sortOptionsIsOpen={sortOptionsIsOpen}
              totalProductCount={props.totalProductCount}
              sortOptions={props.sortOptions}
              alignBottom={showSelectedFacets}
              onChangeSort={onChangeSort}
              sortRef={sortRef}
            />
          )}
        </Content>
      </Base>
      <FacetFlyout disableFreezeScroll zIndex={ZIndex.Overlay} position={Position.Left} visible={open} onClose={close}>
        <Entry css={{ zIndex: 1 }} ref={enterRef}>
          {openFacetKey ? facetHeader : facetRootHeader}
          <Body>{openFacetKey ? facet : facetList}</Body>
        </Entry>
        <Entry ref={leavingRef}>
          {openFacetKey ? facetRootHeader : facetHeader}
          <Body>{openFacetKey ? facetList : facet}</Body>
        </Entry>
        <Footer>
          <FooterButton
            size={FieldSize.Small}
            as={Theme.Monochrome}
            onClick={onClearFacets}
            {...NnTestIdProps.FilterFlyoutResetButton}
          >
            {translate(t => t.productListing.clearFilterButtonText)}
          </FooterButton>
          <FooterButton
            size={FieldSize.Small}
            onClick={close}
            as={isSvenssons ? Theme.Primary : Theme.Secondary}
            {...NnTestIdProps.FilterFlyoutConfirmButton}
          >
            {translate(p => p.productListing.productHitsButtonText, {
              ['product_count']: props.totalProductCount,
            })}
          </FooterButton>
        </Footer>
      </FacetFlyout>
      {!isDesktop && (
        <div ref={sortRef}>
          <Sort
            sortOptions={props.sortOptions}
            onChangeSort={onChangeSort}
            isOpen={sortOptionsIsOpen}
            setIsOpen={setOptionsIsOpen}
          />
        </div>
      )}
    </>
  );
});

const Base = styled.div({
  transition: {
    property: 'all',
    duration: 200,
    timingFunction: 'ease-out',
  },
  position: 'relative',
  zIndex: ZIndex.FilterButton,
  backgroundColor: 'white',
  transform: 'translateY(0%)',
});

const stickyDecorator = styled(
  createMinLargeViewportDecorator({
    top: DESKTOP_HEADER_HEIGHT,
  }),
  {
    position: 'sticky',
    top: HEADER_HEIGHT,
  },
);

const stickyWithQuickSearchDecorator = styled(
  createMaxSmallViewportDecorator({ top: HEADER_HEIGHT_WITH_SEARCH, position: 'sticky' }),
);

const hideElement = styled({
  transform: 'translateY(-100%)',
});

const Content = styled.div(
  desktopEdgeMarginDecorator,
  createMinLargeViewportDecorator({
    gridTemplateAreas: '"facet highlight sort"',
    rowGap: t => t.sharedSpacing.m,
  }),
  {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gridTemplateAreas: '"facet highlight" "sort sort"',
    gap: t => t.sharedSpacing.xs,
    padding: { y: t => t.sharedSpacing.m },
    alignItems: 'center',
    backgroundColor: white,
    width: '100%',
  },
);

const gridAreaDecorator = styled({
  gridTemplateAreas: '"facet sort"',
});

const selectedgridAreaDecorator = styled(
  createMinLargeViewportDecorator({
    gridTemplateAreas: '"facet highlight sort" "selectedFacets selectedFacets sort"',
  }),
  {
    gridTemplateAreas: '"facet highlight" "selectedFacets selectedFacets" "sort sort"',
  },
);

const FacetFlyout = styled(Flyout, {
  overflow: 'hidden',
});

const Header = styled.div({
  height: headerHeight,
  backgroundColor: t => t.facets?.flyoutHeader?.color,
  display: 'grid',
  gridTemplateAreas: '"back text close"',
  gridTemplateColumns: '51px 1fr 51px',
  alignItems: 'center',
});

const HeaderText = styled.h1({
  textAlign: 'center',
  marginBottom: 0,
  gridArea: 'text',
  fontSize: t => t.facets?.flyoutHeader?.fontSize,
  fontWeight: t => t.facets?.flyoutHeader?.fontWeight,
});

const CloseButton = styled(Button, createButtonHoverDecorator({ backgroundColor: 'transparent' }), {
  marginLeft: 'auto',
  gridArea: 'close',
});

const Close = styled(CloseIcon, {
  width: 27,
  height: 27,
});

const Back = styled(Button, {
  gridArea: 'back',
});

const FacetsButton = styled(
  Button,
  createMaxSmallViewportDecorator({
    marginLeft: t => t.sharedSpacing.xs,
  }),
  {
    flexShrink: 0,
    display: 'flex',
    gap: t => t.sharedSpacing.xs,
    alignItems: 'center',
    gridArea: 'facet',
    padding: { x: t => t.sharedSpacing.xs },
    fontSize: t => t.sharedFontSize.s,
  },
);

const buttonDecorator = styled({
  padding: { right: t => t.sharedSpacing.s },
});

const FilterButtonIcon = styled(FilterIcon, {
  width: 24,
  height: 24,
});

const Entry = styled.div({
  position: 'absolute',
  width: '100%',
  height: 'calc(100% - 80px)',
  top: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  willChange: 'opacity, transform',
});

const Body = styled.div(overflowYDecorator, scrollbarDecorator, {
  height: '100%',
});

const Footer = styled.div({
  position: 'fixed',
  bottom: t => t.sharedSpacing.l,
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gridGap: t => t.sharedSpacing.m,
  width: '100%',
  padding: { x: t => t.sharedSpacing.l },
});

const FacetList = styled.ul(bareListDecorator);

const FooterButton = styled(Button, {
  fontSize: t => t.fontSize.xs,
});

const paddingDecorator = styled(
  createMaxSmallViewportDecorator({
    paddingBottom: t => t.sharedSpacing.xs,
  }),
);
